import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { formatAmount, formatDate } from "../../utils/utils";

const chipSx = (val) =>
  ({
    paid: { bgcolor: "#d1fae5", color: "#065f46" },
    pending: { bgcolor: "#fef3c7", color: "#92400e" },
    failed: { bgcolor: "#fee2e2", color: "#991b1b" },
    cart: { bgcolor: "#dbeafe", color: "#1d4ed8" },
    booking: { bgcolor: "#ede9fe", color: "#5b21b6" },
  }[val] || { bgcolor: "#f3f4f6", color: "#374151" });

function Row({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", py: 0.75 }}>
      <Typography sx={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#111827", textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Box>
  );
}

function CartItems({ goods }) {
  if (!Array.isArray(goods) || goods.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: "#9ca3af", textAlign: "center", py: 2 }}>
        No item details recorded
      </Typography>
    );
  }

  return (
    <Box>
      {/* Column headers */}
      <Box sx={{ display: "flex", gap: 1, pb: 1, borderBottom: "1px solid #e5e7eb" }}>
        {["Item", "Qty", "Price"].map((h) => (
          <Typography
            key={h}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              flex: h === "Item" ? 1 : "none",
              width: h !== "Item" ? 48 : undefined,
              textAlign: h === "Item" ? "left" : "right",
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>

      {goods.map((item, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1.5,
            borderBottom: i < goods.length - 1 ? "1px solid #f3f4f6" : "none",
          }}
        >
          {/* Image */}
          {item.imgSrc && (
            <Box
              component="img"
              src={item.imgSrc}
              alt={item.name}
              sx={{ width: 36, height: 36, objectFit: "cover", borderRadius: "6px", border: "1px solid #e5e7eb", flexShrink: 0 }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          <Typography sx={{ flex: 1, fontSize: 13, color: "#374151", fontWeight: 500 }}>
            {item.name || item.title || "—"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6b7280", width: 48, textAlign: "right" }}>
            ×{item.quantity || 1}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827", width: 48, textAlign: "right" }}>
            {item.price ? formatAmount(item.price) : "—"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CustomerInfo({ userId }) {
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (!userId) return;
    getDoc(doc(db, "users", userId))
      .then((snap) => { if (snap.exists()) setCustomer(snap.data()); })
      .catch(console.error);
  }, [userId]);

  if (!customer) return null;
  const name = [customer.firstname, customer.lastname].filter(Boolean).join(" ") || "—";
  const phone = customer.phone || customer.phoneNumber || "—";

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
        Customer
      </Typography>
      <Row label="Name" value={name} />
      <Row label="Phone" value={phone} />
    </>
  );
}

function BookingDetail({ bookId, txData }) {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bookId) return;
    setLoading(true);
    getDocs(query(collection(db, "booking-list"), where("paymentIntentId", "==", txData?.paymentIntentId || "")))
      .then((snap) => {
        if (!snap.empty) setBooking({ id: snap.docs[0].id, ...snap.docs[0].data() });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [bookId, txData?.paymentIntentId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} sx={{ color: "#194049" }} />
      </Box>
    );
  }

  const b = booking;
  if (!b) {
    return (
      <Typography sx={{ fontSize: 13, color: "#9ca3af", textAlign: "center", py: 2 }}>
        Booking details not found
      </Typography>
    );
  }

  const slotDate = b.time ? new Date(b.time) : null;

  return (
    <Box>
      <Row label="Category" value={b.category || "—"} />
      <Row label="Service" value={b.service || "—"} />
      <Row label="Slot" value={slotDate ? `${formatDate(slotDate)} · ${slotDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—"} />
    </Box>
  );
}

export default function TransactionDetailModal({ transaction, onClose }) {
  if (!transaction) return null;

  const tx = transaction;
  const isBooking = tx.type === "booking";
  const viewer = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = viewer?.role === "admin";
  const date =
    tx.createdAt?.toDate?.() ||
    (tx.createdAt?.seconds ? new Date(tx.createdAt.seconds * 1000) : null) ||
    (tx.purchase_time ? new Date(tx.purchase_time) : null);

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, alignItems: "flex-start", pt: { xs: 4, md: 8 } }}
    >
      <Paper
        onClick={(e) => e.stopPropagation()}
        elevation={4}
        sx={{
          width: { xs: "92vw", sm: 480 },
          maxHeight: "80vh",
          overflowY: "auto",
          borderRadius: "16px",
          p: 0,
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            bgcolor: "#fff",
            zIndex: 1,
            borderRadius: "16px 16px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "8px",
                bgcolor: isBooking ? "#ede9fe" : "#dbeafe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: isBooking ? "#5b21b6" : "#1d4ed8",
              }}
            >
              {isBooking ? <CalendarMonthIcon sx={{ fontSize: 18 }} /> : <ShoppingBagIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                {isBooking ? "Booking" : "Order"} Details
              </Typography>
              <Chip
                label={tx.status || "pending"}
                size="small"
                sx={{ ...chipSx(tx.status), fontSize: 10, height: 18, mt: 0.25 }}
              />
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Transaction summary */}
          <Box sx={{ mb: 2 }}>
            <Row label="Reference" value={tx.paymentIntentId || "—"} />
            <Row label="Amount" value={formatAmount(tx.amount)} />
            <Row label="Type" value={tx.type || "—"} />
            <Row label="Date" value={date ? formatDate(date) : "—"} />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Type-specific section */}
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            {isBooking ? "Booking Info" : "Items Purchased"}
          </Typography>

          {isBooking ? (
            <BookingDetail bookId={tx.bookId} txData={tx} />
          ) : (
            <CartItems goods={tx.goods} />
          )}
          {isAdmin && tx.userId && <CustomerInfo userId={tx.userId} />}
        </Box>
      </Paper>
    </Backdrop>
  );
}
